import type { ActorDto } from '@newo/api-client';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PATHS, runtimeConfig } from '@/constants';
import { useRestoreProcess } from '@/hooks/useRestoreProcess.ts';
import { normalizeRoutePath } from '@/routes.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { useAgentTraining } from '@/store/agent-training.ts';
import { AgentCommand } from '@/types/agent-realtime.ts';
import { AgentCreationStep } from '@/types/agent-training.ts';
import { type InitSuccessCb, useInitialize } from '@/utils/api.ts';

const useApp = (actor?: ActorDto) => {
	const isAppInitialized = useRef(false);

	useRestoreProcess();

	const { step } = useAgentTraining();
	const location = useLocation();

	const isFirstStep = runtimeConfig.isRemoteBuild
		? step === AgentCreationStep.SOURCE_SELECT
		: [
				normalizeRoutePath(PATHS.index),
				normalizeRoutePath(PATHS.creatorIndex),
				PATHS.remoteCreatorIndex
			].includes(normalizeRoutePath(location.pathname));

	const handleSuccess = useCallback(
		({ connectorIdn, externalId, clientSecret }: InitSuccessCb) => {
			if (!clientSecret || !externalId || !connectorIdn) {
				return;
			}

			agentTrainingRealtime.init(
				{
					clientSecret,
					externalId,
					connectorIdn
				},
				AgentCommand.OnboardingFinished
			);
		},
		[]
	);

	const { mutateAsync: initApp } = useInitialize(false, actor);

	// biome-ignore lint/correctness/useExhaustiveDependencies: no need for additional deps
	useEffect(() => {
		if (isFirstStep) {
			isAppInitialized.current = true;
			return;
		}

		if (!isAppInitialized.current) {
			isAppInitialized.current = true;

			initApp(undefined, {
				onSuccess: handleSuccess
			});
		}
	}, [isFirstStep, initApp]);
};

export default useApp;
